import * as React from "react";
import * as PropTypes from "prop-types";
import clsx from "clsx";
import * as theme from "./theme.scss";
import {RouteComponentProps} from "react-router";
import {AppBar} from "@vidazoo/ui/lib/components/appBar";
import {UserMenu} from "@vidazoo/ui/lib/components/userMenu";
import {Menu, MenuItem} from "@vidazoo/ui/lib/components/menu";
import {MenuTree, IMenuNode} from "@vidazoo/ui/lib/components/menuTree";
import {UserRole, ThemeSwitch, VidazooApps, NotificationPanel, IDictionary} from "@vidazoo/ui-framework";
import {appUrlsService} from "common/services";
import AccountsList from "../accountsList";
import routes from "common/routes";
import {AuthScope, DEFAULT_ACCESS, NetworkFeatures, TimePresets} from "common/enums";
import {ButtonsGroup, GroupButton, VidazooHeaderLogo} from "@vidazoo/ui";
import {sessionStore, userNotificationsStore} from "../../../../common/stores";
import IMenuEntry from "../../../../common/interfaces/IMenuEntry";
import {Twirl as Hamburger} from "hamburger-react";
import {Link} from "react-router-dom";
import IDashboard from "../../../dashboard/interfaces/IDashboard";
import VidazooLogo from "../../../../common/logo";

const USER_MENU_LINKS = [
	{label: "Terms & Conditions", native: true, linkProps: {href: (window as any).__TERMS_URL__, target: "_blank"}},
	{label: "Privacy Policy", native: true, linkProps: {href: (window as any).__PRIVACY_URL__, target: "_blank"}}
];

export interface IAppHeaderProps extends RouteComponentProps<any> {
	userName?: string;
	userEmail?: string;
	userScopes?: string[];
	userAvatarUrl?: string;
	onSignOut?: () => void;
	openMyProfile?: () => void;
	setTimePreset: (name: string) => void;
	timePreset: TimePresets;
	userId?: string;
	cloudDashboardsStars: string[];
	dashboardsById: IDictionary<IDashboard>;
	apps?: any[];
	getApps?: () => void;
}

export interface IAppHeaderState {
	mounted: boolean;
	hideAppHeader: boolean;
	scrollY: number;
	isNavbarMobileOpen: boolean;
}

export default class AppHeader extends React.Component<IAppHeaderProps, IAppHeaderState> {

	public static defaultProps: Partial<IAppHeaderProps> = {};

	constructor(props, context) {
		super(props, context);

		this.state = {isNavbarMobileOpen: false, mounted: false, hideAppHeader: false, scrollY: window.scrollY};
		if ((window as any).__device === "mobile") {
			addEventListener("scroll", this.onScroll);
		}
	}

	public componentDidMount() {
		if (this.props.getApps) {
			this.props.getApps();
		}
		window.requestAnimationFrame(() => {
			this.setState({mounted: true});
		});
	}

	private get menuRoutes(): IMenuEntry[] {
		const clonedRoutes: IMenuEntry[] = JSON.parse(JSON.stringify(routes));
		const dashboardsRoutes = clonedRoutes.find((route) => route.id === "dashboard");
		if (dashboardsRoutes) {
			for (const dashboardStarId of this.props.cloudDashboardsStars) {
				if (this.props.dashboardsById[dashboardStarId]) {
					dashboardsRoutes.items.push({
						title: this.props.dashboardsById[dashboardStarId].name,
						link: {to: appUrlsService.editDashboard(dashboardStarId)},
						access: DEFAULT_ACCESS,
					});
				}
			}
		}

		return clonedRoutes;
	}

	private toggleNavbarMobileOpen = () => {
		this.setState({isNavbarMobileOpen: !this.state.isNavbarMobileOpen});
	};

	private onScroll = (e) => {
		this.setState({hideAppHeader: (this.state.scrollY < window.scrollY), scrollY: window.scrollY});
	};

	public onLogoClick = () => {
		const {history} = this.props;

		history.push(appUrlsService.dashboards());
	};

	private onLogout = () => {
		const {onSignOut} = this.props;

		onSignOut();
	};

	private reduceRouteItems(items: IMenuEntry[]): IMenuNode[] {
		return items.reduce((result, item) => {
			if (sessionStore.isUserAllowed(item.scope)) {
				if (item.items) {
					item.items = this.reduceRouteItems(item.items);
					if (item.items.length === 0) {
						return result;
					}
				}

				return result.concat(item);
			}

			return result;
		}, []);
	}

	public renderMobileNavbar(items: IMenuNode[]): JSX.Element {
		const {isNavbarMobileOpen} = this.state;
		const classes = clsx(theme.navbar, {
			[theme.open]: isNavbarMobileOpen
		});
		return (
			<div className={theme.mobileNavbar}>
				<div className={theme.hamburger}>
					<Hamburger toggled={isNavbarMobileOpen} toggle={this.toggleNavbarMobileOpen} color="white"/>
				</div>
				<div className={classes}>
					{items.map((item) => {
						return (
							<Link onClick={this.toggleNavbarMobileOpen} to={item.link.to}>{item.title}</Link>
						);
					})}
				</div>
			</div>
		);
	}

	private onTimePresetChange = (name: string) => {
		this.props.setTimePreset(name);
	};

	public render(): JSX.Element {

		const {
			location,
			userName,
			userEmail,
			userAvatarUrl,
			openMyProfile,
			userScopes,
			timePreset,
			userId
		} = this.props;

		const {mounted} = this.state;

		const classes = clsx(theme.header, {
			[theme.mounted]: (!this.state.hideAppHeader && mounted),
			[theme.hide]: this.state.hideAppHeader
		});

		return (
			<AppBar enableSticky className={classes}>
				<div className={theme.inner}>
					{((window as any).__device !== "mobile" && window.innerWidth > 600) || !location.pathname.includes("dashboard") ? (
						<div className="flex align-center">
							<div onClick={this.onLogoClick} className={theme.logo}>
								<VidazooLogo width={181} height={66} />
							</div>
						</div>
					) : null}
					{((window as any).__device !== "mobile" && window.innerWidth > 600) ? (
						<MenuTree className="flex-1" items={this.reduceRouteItems(this.menuRoutes)}/>
					) : null}

					{location.pathname.includes("dashboard") || location.pathname.includes("chart") ? (
						<ButtonsGroup className={theme.timePreset} active={timePreset}
									  onChange={this.onTimePresetChange}>
							<GroupButton className={theme.groupButton} name={TimePresets.TwoHours}>Two
								Hours</GroupButton>
							<GroupButton className={theme.groupButton} name={TimePresets.FourHours}>Four
								Hours</GroupButton>
							<GroupButton className={theme.groupButton} name={TimePresets.Today}>Today</GroupButton>
							<GroupButton className={theme.groupButton}
										 name={TimePresets.Yesterday}>Yesterday</GroupButton>
						</ButtonsGroup>
					) : null}
					{((window as any).__device !== "mobile" && window.innerWidth > 600) ? (
						<>
							<ThemeSwitch className={theme["theme-switch"]}/>
							<AccountsList/>
							<NotificationPanel userId={userId} userNotificationsStore={userNotificationsStore}/>
							<div className="flex align-center">
								<VidazooApps apps={this.props.apps} userScopes={userScopes} currentScope={AuthScope.BI_DASHBOARD}/>
								<UserMenu links={USER_MENU_LINKS} smallAvatar darkAvatar userName={userName}
										  userEmail={userEmail} avatarUrl={userAvatarUrl} className={theme.user}>
									<MenuItem iconName="icon-settings" label="My Profile" onClick={openMyProfile}/>
									<MenuItem iconName="icon-sign-out" label="Sign Out" onClick={this.onLogout}/>
								</UserMenu>
							</div>
						</>
					) : this.renderMobileNavbar(this.menuRoutes)}
				</div>
			</AppBar>
		);
	}
}
